import clsx from 'clsx';
import { type CSSProperties, forwardRef } from 'react';

import { skeletonPlaceholderCSS, skeletonSvgCSS } from './Skeleton.sty.js';
import { SkeletonDrawer, SkeletonGradient } from './utils.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useId } from '../../core/hooks/useId.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';

const COMPONENT_NAME = 'Skeleton';

/**
 * Accepted properties for Skeleton
 * @public
 */
export interface SkeletonProps
  extends WithChildren,
    StylingProps,
    DataTestId,
    AriaLabelingProps,
    BehaviorTrackingProps {
  /**
   * Defines the shape of the Skeleton.
   * @defaultValue 'default'
   */
  variant?: 'default' | 'rounded';
  /**
   * Width of the Skeleton placeholder.
   * @defaultValue 100%
   */
  width?: CSSProperties['width'];
  /**
   * Height of the Skeleton placeholder.
   * @defaultValue 100%
   */
  height?: CSSProperties['height'];
}

/**
 * The `Skeleton` component renders a colored block with an animated background, indicating that something is loading.
 * @public
 */
export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(
  (props, forwardedRef) => {
    const {
      children,
      variant = 'default',
      width = '100%',
      height = '100%',
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      ...remainingProps
    } = props;

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);
    const ariaLabelingProps = useAriaLabelingProps(restProps);
    const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);

    const gradientId = useId();

    return (
      <div
        ref={forwardedRef}
        className={clsx(skeletonPlaceholderCSS({ variant }), consumerClassName)}
        style={{ width, height, ...consumerStyle }}
        data-testid={dataTestId}
        {...ariaLabelingProps}
        {...behavioralTrackingProps}
        {...forwardProps}
        data-dt-component={COMPONENT_NAME}
      >
        <svg className={skeletonSvgCSS}>
          <defs>
            <SkeletonGradient id={gradientId} />
          </defs>
          <SkeletonDrawer gradientId={gradientId} />
        </svg>
        {children}
      </div>
    );
  },
);

(Skeleton as typeof Skeleton & { displayName: string }).displayName =
  COMPONENT_NAME;
